import { useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Banknote, CreditCard, HandCoins, Landmark } from 'lucide-react'
import { exceedsCreditLimit, formatMoney, PAYMENT_METHODS, toMajor, toMinor, type PaymentMethod } from '@najah/core'
import { Alert, Button, Dialog, Input, Select, cn } from '@najah/ui'
import { customersApi } from '../../lib/customersApi'

interface Props {
  open: boolean
  onClose: () => void
  total: number
  /** الزبون مطلوب عند البيع الآجل — الدين لا يُسجَّل بلا صاحب */
  onConfirm: (payments: { method: PaymentMethod; amount: number }[], customerId: string | null) => void
  submitting: boolean
}

const METHOD_ICONS: Record<PaymentMethod, typeof Banknote> = {
  CASH: Banknote,
  CARD: CreditCard,
  TRANSFER: Landmark,
  CREDIT: HandCoins,
}

/** مبالغ سريعة شائعة في الدرج الجزائري */
const QUICK_NOTES = [100, 200, 500, 1000, 2000]

export function PaymentDialog({ open, onClose, total, onConfirm, submitting }: Props) {
  const [method, setMethod] = useState<PaymentMethod>('CASH')
  const [received, setReceived] = useState('')
  const [customerId, setCustomerId] = useState('')

  useEffect(() => {
    if (!open) return
    setMethod('CASH')
    setReceived('')
    setCustomerId('')
  }, [open])

  // تُجلب عند فتح خيار الآجل فقط — لا داعي لتحميلها في كل بيع نقدي
  const { data: customers } = useQuery({
    queryKey: ['customers', 'options'],
    queryFn: customersApi.options,
    enabled: open && method === 'CREDIT',
  })

  const { data: customerDetail } = useQuery({
    queryKey: ['customers', 'detail', customerId],
    queryFn: () => customersApi.get(customerId),
    enabled: !!customerId && method === 'CREDIT',
  })

  const overLimit =
    method === 'CREDIT' && customerDetail
      ? exceedsCreditLimit(customerDetail.balance, customerDetail.creditLimit, total)
      : false

  const receivedMinor = received ? toMinor(Number(received) || 0) : 0
  // غير النقدي يُدفع بالضبط: لا يمكن ردّ باقٍ من بطاقة أو تحويل
  const effectivePaid = method === 'CASH' ? receivedMinor : total
  const change = Math.max(0, effectivePaid - total)
  const short = Math.max(0, total - effectivePaid)

  const canConfirm = useMemo(() => {
    // الآجل يتطلّب زبوناً ولا يتجاوز سقفه
    if (method === 'CREDIT') return !!customerId && !overLimit
    if (method !== 'CASH') return true
    return receivedMinor >= total
  }, [method, receivedMinor, total, customerId, overLimit])

  function confirm() {
    if (method === 'CASH') {
      onConfirm([{ method: 'CASH', amount: receivedMinor }], null)
    } else {
      onConfirm([{ method, amount: total }], method === 'CREDIT' ? customerId : null)
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      size="md"
      title="الدفع"
      dismissible={!submitting}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={submitting}>إلغاء</Button>
          <Button size="lg" onClick={confirm} loading={submitting} disabled={!canConfirm}>
            إتمام البيع
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        <div className="rounded-xl bg-primary-subtle px-5 py-4 text-center">
          <p className="text-sm text-primary-subtle-fg">الإجمالي المطلوب</p>
          <p className="mt-1 text-3xl font-bold text-primary-subtle-fg" dir="ltr">{formatMoney(total)}</p>
        </div>

        <div className="grid grid-cols-4 gap-2">
          {(Object.keys(PAYMENT_METHODS) as PaymentMethod[]).map((m) => {
            const Icon = METHOD_ICONS[m]
            return (
              <button
                key={m}
                type="button"
                onClick={() => setMethod(m)}
                className={cn(
                  'flex flex-col items-center gap-1.5 rounded-xl border px-2 py-3 text-xs font-medium transition-colors',
                  method === m
                    ? 'border-primary bg-primary-subtle text-primary-subtle-fg'
                    : 'border-border text-fg-muted hover:bg-surface-hover',
                )}
              >
                <Icon className="size-5" />
                {PAYMENT_METHODS[m]}
              </button>
            )
          })}
        </div>

        {method === 'CASH' ? (
          <div className="space-y-3">
            <Input
              label="المبلغ المستلم" type="number" min="0" step="0.01" ltr autoFocus
              value={received}
              onChange={(e) => setReceived(e.target.value)}
              hint="دج"
            />

            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setReceived(String(toMajor(total)))}
                className="rounded-lg border border-border px-3 py-1.5 text-xs font-medium text-fg-muted transition-colors hover:bg-surface-hover"
              >
                بالضبط
              </button>
              {QUICK_NOTES.map((note) => (
                <button
                  key={note}
                  type="button"
                  onClick={() => setReceived(String((Number(received) || 0) + note))}
                  className="rounded-lg border border-border px-3 py-1.5 text-xs font-medium text-fg-muted transition-colors hover:bg-surface-hover"
                  dir="ltr"
                >
                  +{note}
                </button>
              ))}
            </div>

            {receivedMinor > 0 && (
              <div
                className={cn(
                  'rounded-lg px-4 py-3 text-center',
                  short > 0 ? 'bg-danger-subtle' : 'bg-success-subtle',
                )}
              >
                {short > 0 ? (
                  <>
                    <p className="text-sm text-danger">ناقص</p>
                    <p className="text-2xl font-bold text-danger" dir="ltr">{formatMoney(short)}</p>
                  </>
                ) : (
                  <>
                    <p className="text-sm text-success">الباقي للزبون</p>
                    <p className="text-2xl font-bold text-success" dir="ltr">{formatMoney(change)}</p>
                  </>
                )}
              </div>
            )}
          </div>
        ) : method === 'CREDIT' ? (
          <div className="space-y-3">
            <Select
              label="الزبون" required
              value={customerId} onChange={(e) => setCustomerId(e.target.value)}
              options={(customers ?? []).map((c) => ({ value: c.id, label: `${c.fullName} — ${c.phone}` }))}
              placeholder="اختر زبوناً"
              hint="الدين يُسجَّل على حسابه ويظهر في كشفه"
            />

            {customerDetail && (
              <div className="rounded-lg bg-bg-subtle px-4 py-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-fg-muted">دينه الحالي</span>
                  <span className="font-semibold text-fg" dir="ltr">{formatMoney(customerDetail.balance)}</span>
                </div>
                <div className="mt-1 flex justify-between">
                  <span className="text-fg-muted">بعد هذه الفاتورة</span>
                  <span className="font-semibold text-warning" dir="ltr">
                    {formatMoney(customerDetail.balance + total)}
                  </span>
                </div>
                {customerDetail.creditLimit > 0 && (
                  <div className="mt-1 flex justify-between border-t border-border pt-1">
                    <span className="text-fg-muted">سقف الدين</span>
                    <span className="text-fg-muted" dir="ltr">{formatMoney(customerDetail.creditLimit)}</span>
                  </div>
                )}
              </div>
            )}

            {overLimit && (
              <Alert tone="danger" title="يتجاوز سقف الدين">
                هذه الفاتورة تتجاوز السقف المسموح لهذا الزبون. ارفع السقف من صفحة الزبائن،
                أو حصّل جزءاً من دينه أولاً.
              </Alert>
            )}

            {!customerId && (
              <Alert tone="warning">
                البيع الآجل يتطلّب زبوناً مسجّلاً — الدين لا يُسجَّل بلا صاحب.
                أضف الزبون من صفحة «الزبائن» إن لم يكن مسجّلاً.
              </Alert>
            )}
          </div>
        ) : (
          <Alert tone="info">
            سيُسجَّل المبلغ <span className="font-semibold" dir="ltr">{formatMoney(total)}</span> كاملاً
            بطريقة «{PAYMENT_METHODS[method]}».
          </Alert>
        )}
      </div>
    </Dialog>
  )
}
